import { useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  BarChart3,
  Download,
  Menu,
  RefreshCw,
  Search,
  Store,
  Trash2,
  TrendingUp,
  Users,
  WalletCards,
} from "lucide-react";
import type { DouyinPerformanceRecord, PerformanceStore, TmallPerformanceRecord } from "@ailan/contracts";
import {
  clearAllDemoData,
  getDouyinDashboard,
  getKuaishouDashboard,
  getPddDashboard,
  getTmallDashboard,
} from "../api/client";
import {
  DashboardPlatformTabs,
  type DashboardView,
} from "../components/DashboardPlatformTabs";
import { KuaishouDashboardPage } from "./KuaishouDashboardPage";
import { PddDashboardPage } from "./PddDashboardPage";
import { TmallDashboardPage } from "./TmallDashboardPage";
import { DouyinDashboardPage } from "./DouyinDashboardPage";
import { MetricDetailModal, type MetricDetailRow } from "../components/MetricDetailModal";

type PlatformId = Exclude<DashboardView, "all">;
type OverviewMetric = "platformCount" | "storeCount" | "agentCount" | "receptionCount" | "orderCount" | "netSalesAmount";
type OverviewPeriodMode = "latest" | "day" | "custom";
type PlatformRecord = {
  platform: PlatformId;
  storeName: string;
  agentName: string;
  identityNames: string[];
  periodEnd: string;
  receptionCount: number;
  inquiryCount: number | null;
  orderCount: number | null;
  netSalesAmount: number;
  conversionRate: number;
  conversionAvailable: boolean;
  conversionWeight: number;
  avgReplySeconds: number;
  responseWeight: number;
  responseBase: number;
  satisfactionRate: number | null;
  satisfactionWeight: number;
};
type PlatformStoreRow = PerformanceStore & {
  platform: PlatformId;
  latestDate: string;
  hasData: boolean;
};
type Summary = {
  receptionCount: number;
  inquiryCount: number;
  inquiryAvailable: boolean;
  orderCount: number;
  orderAvailable: boolean;
  netSalesAmount: number;
  conversionRate: number;
  conversionAvailable: boolean;
  avgReplySeconds: number;
  satisfactionRate: number;
  satisfactionAvailable: boolean;
};

const platformLabels: Record<PlatformId, string> = {
  tmall: "天猫",
  kuaishou: "快手",
  pdd: "拼多多",
  douyin: "抖音",
};
const overviewMetricLabels: Record<OverviewMetric, string> = { platformCount: "平台数", storeCount: "店铺数", agentCount: "客服数", receptionCount: "有效接待人数", orderCount: "下单人数", netSalesAmount: "净销售额" };

function pddIdentityName(agentName: string) {
  return agentName.match(/^[A-Za-z]*(\d{11})$/)?.[1] ?? agentName;
}

const douyinUnifiedAgentNames = ["小婷", "小颖", "小石", "小美", "甜甜", "小君"] as const;

function douyinIdentityName(agentName: string) {
  return douyinUnifiedAgentNames.find((name) => agentName.includes(name)) ?? agentName;
}

function normalizeCommerceRecord(
  item: TmallPerformanceRecord,
  platform: Exclude<PlatformId, "douyin">,
): PlatformRecord {
  const hasSatisfaction = platform !== "pdd";
  const identityName = platform === "pdd" ? pddIdentityName(item.agentName) : item.agentName;
  return {
    platform,
    storeName: item.storeName,
    agentName: item.agentName,
    identityNames: [identityName],
    periodEnd: item.periodEnd,
    receptionCount: item.receptionCount,
    inquiryCount: item.inquiryCount,
    orderCount: item.orderCount,
    netSalesAmount: item.netSalesAmount,
    conversionRate: item.conversionRate,
    conversionAvailable: !item.conversionDelayed,
    conversionWeight: item.conversionBaseCount,
    avgReplySeconds: item.avgReplySeconds,
    responseWeight: item.avgReplySeconds * Math.max(item.receptionCount, 1),
    responseBase: item.receptionCount,
    satisfactionRate: hasSatisfaction ? item.satisfactionRate : null,
    satisfactionWeight: hasSatisfaction ? item.reviewCount : 0,
  };
}

function normalizeDouyinRecord(item: DouyinPerformanceRecord): PlatformRecord {
  const weight = Math.max(item.receptionCount, 1);
  return {
    platform: "douyin",
    storeName: item.storeName,
    agentName: item.agentName,
    identityNames: [douyinIdentityName(item.agentName), item.subAccountName].filter(Boolean),
    periodEnd: item.periodEnd,
    receptionCount: item.receptionCount,
    inquiryCount: null,
    orderCount: null,
    netSalesAmount: item.netSalesAmount,
    conversionRate: item.inquiryConversionRate,
    conversionAvailable: true,
    conversionWeight: weight,
    avgReplySeconds: item.avgReplySeconds,
    responseWeight: item.avgReplySeconds * weight,
    responseBase: weight,
    satisfactionRate: item.satisfactionRate,
    satisfactionWeight: weight,
  };
}

function summarize(records: PlatformRecord[]): Summary {
  const receptionCount = records.reduce((sum, item) => sum + item.receptionCount, 0);
  const inquiryRecords = records.filter((item) => item.inquiryCount !== null);
  const orderRecords = records.filter((item) => item.orderCount !== null);
  const inquiryCount = inquiryRecords.reduce((sum, item) => sum + item.inquiryCount!, 0);
  const orderCount = orderRecords.reduce((sum, item) => sum + item.orderCount!, 0);
  const netSalesAmount = records.reduce((sum, item) => sum + item.netSalesAmount, 0);
  const conversionRecords = records.filter((item) => item.conversionAvailable);
  const conversionBase = conversionRecords.reduce((sum, item) => sum + item.conversionWeight, 0);
  const conversionWeight = conversionRecords.reduce(
    (sum, item) => sum + item.conversionRate * item.conversionWeight,
    0,
  );
  const responseWeight = records.reduce((sum, item) => sum + item.responseWeight, 0);
  const responseBase = records.reduce((sum, item) => sum + item.responseBase, 0);
  const satisfactionRecords = records.filter((item) => item.satisfactionRate !== null);
  const satisfactionBase = satisfactionRecords.reduce((sum, item) => sum + item.satisfactionWeight, 0);
  const satisfactionWeight = satisfactionRecords.reduce(
    (sum, item) => sum + item.satisfactionRate! * item.satisfactionWeight,
    0,
  );

  return {
    receptionCount,
    inquiryCount,
    inquiryAvailable: inquiryRecords.length > 0,
    orderCount,
    orderAvailable: orderRecords.length > 0,
    netSalesAmount,
    conversionRate: conversionBase ? conversionWeight / conversionBase : 0,
    conversionAvailable: conversionBase > 0,
    avgReplySeconds: responseBase ? responseWeight / responseBase : 0,
    satisfactionRate: satisfactionBase ? satisfactionWeight / satisfactionBase : 0,
    satisfactionAvailable: satisfactionBase > 0,
  };
}

function groupUniqueAgents(records: PlatformRecord[]) {
  const parents = records.map((_, index) => index);
  const find = (index: number): number => {
    if (parents[index] !== index) parents[index] = find(parents[index]!);
    return parents[index]!;
  };
  const union = (left: number, right: number) => {
    const leftRoot = find(left);
    const rightRoot = find(right);
    if (leftRoot !== rightRoot) parents[rightRoot] = leftRoot;
  };
  const ownerByName = new Map<string, number>();

  records.forEach((record, index) => {
    record.identityNames.forEach((rawName) => {
      const name = rawName.trim();
      if (!name) return;
      const owner = ownerByName.get(name);
      if (owner === undefined) ownerByName.set(name, index);
      else union(index, owner);
    });
  });

  const groups = new Map<number, PlatformRecord[]>();
  records.forEach((record, index) => {
    const root = find(index);
    groups.set(root, [...(groups.get(root) ?? []), record]);
  });

  return [...groups.entries()].map(([root, items]) => ({
    key: `${items[0]!.platform}:${root}`,
    platform: items[0]!.platform,
    names: [...new Set(items.flatMap((item) => item.identityNames).filter(Boolean))],
    stores: [...new Set(items.map((item) => item.storeName))],
  }));
}

function formatNumber(value: number) {
  return new Intl.NumberFormat("zh-CN").format(Math.round(value));
}

function formatDecimal(value: number) {
  return (Math.trunc(value * 100) / 100).toFixed(2);
}

function formatMoney(value: number) {
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: "CNY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.trunc(value * 100) / 100);
}

function formatDate(value: string) {
  const [year, month, day] = value.split("-");
  return `${year}年${Number(month)}月${Number(day)}日`;
}

function formatConversion(summary: Summary) {
  return summary.conversionAvailable
    ? `${formatDecimal(summary.conversionRate)}%`
    : "延迟统计";
}

function formatReply(summary: Summary) {
  return summary.receptionCount
    ? `${formatDecimal(summary.avgReplySeconds)}秒`
    : "--";
}

function formatSatisfaction(summary: Summary) {
  return summary.satisfactionAvailable
    ? `${formatDecimal(summary.satisfactionRate)}%`
    : "--";
}

function exportCurrentRecords(records: PlatformRecord[], period: string) {
  const headers = [
    "平台",
    "店铺",
    "客服名称",
    "日期",
    "接待人数",
    "询单人数",
    "下单人数",
    "净销售额",
    "询单最终付款转化率",
    "平均响应时长（秒）",
    "满意度",
  ];
  const rows = records.map((item) => [
    platformLabels[item.platform],
    item.storeName,
    item.agentName,
    item.periodEnd,
    item.receptionCount,
    item.inquiryCount ?? "--",
    item.orderCount ?? "--",
    item.netSalesAmount,
    item.conversionAvailable ? `${item.conversionRate}%` : "延迟统计",
    item.avgReplySeconds,
    item.satisfactionRate === null ? "--" : `${item.satisfactionRate}%`,
  ]);
  const csv =
    "\ufeff" +
    [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => `"${String(cell).replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `全平台客服明细-${period}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function AllPlatformsOverview({
  onMenuOpen,
  platformNav,
}: {
  onMenuOpen: () => void;
  platformNav: ReactNode;
}) {
  const queryClient = useQueryClient();
  const tmall = useQuery({
    queryKey: ["tmall-dashboard"],
    queryFn: getTmallDashboard,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const kuaishou = useQuery({
    queryKey: ["kuaishou-dashboard"],
    queryFn: getKuaishouDashboard,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const pdd = useQuery({
    queryKey: ["pdd-dashboard"],
    queryFn: getPddDashboard,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const douyin = useQuery({
    queryKey: ["douyin-dashboard"],
    queryFn: getDouyinDashboard,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const [selectedDate, setSelectedDate] = useState("");
  const [periodMode, setPeriodMode] = useState<OverviewPeriodMode>("latest");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [search, setSearch] = useState("");
  const [storeSearch, setStoreSearch] = useState("");
  const [metricOpen, setMetricOpen] = useState<OverviewMetric | null>(null);
  const clearDemoMutation = useMutation({
    mutationFn: clearAllDemoData,
    onSuccess: async () => queryClient.invalidateQueries(),
  });

  const recordsByPlatform = useMemo(
    () => ({
      tmall: (tmall.data?.records ?? []).map((item) => normalizeCommerceRecord(item, "tmall")),
      kuaishou: (kuaishou.data?.records ?? []).map((item) => normalizeCommerceRecord(item, "kuaishou")),
      pdd: (pdd.data?.records ?? []).map((item) => normalizeCommerceRecord(item, "pdd")),
      douyin: (douyin.data?.records ?? []).map(normalizeDouyinRecord),
    }),
    [douyin.data?.records, kuaishou.data?.records, pdd.data?.records, tmall.data?.records],
  );
  const storesByPlatform = useMemo<Record<PlatformId, PerformanceStore[]>>(() => ({
    tmall: tmall.data?.stores ?? [],
    kuaishou: kuaishou.data?.stores ?? [],
    pdd: pdd.data?.stores ?? [],
    douyin: douyin.data?.stores ?? [],
  }), [douyin.data?.stores, kuaishou.data?.stores, pdd.data?.stores, tmall.data?.stores]);
  const storeDirectoryRows = useMemo<PlatformStoreRow[]>(() => (
    (Object.keys(storesByPlatform) as PlatformId[]).flatMap((platform) =>
      storesByPlatform[platform].map((store) => {
        const dates = recordsByPlatform[platform]
          .filter((record) => record.storeName === store.name)
          .map((record) => record.periodEnd)
          .sort((left, right) => right.localeCompare(left));
        return { ...store, platform, latestDate: dates[0] ?? "", hasData: dates.length > 0 };
      }),
    )
  ), [recordsByPlatform, storesByPlatform]);
  const filteredStoreRows = useMemo(() => {
    const term = storeSearch.trim().toLowerCase();
    return storeDirectoryRows.filter((store) =>
      !term || `${platformLabels[store.platform]}${store.name}`.toLowerCase().includes(term));
  }, [storeDirectoryRows, storeSearch]);

  const commonDates = useMemo(() => {
    const platformDates = (Object.keys(recordsByPlatform) as PlatformId[]).map(
      (platform) => new Set(recordsByPlatform[platform].map((item) => item.periodEnd)),
    );
    return [...(platformDates[0] ?? new Set<string>())]
      .filter((date) => platformDates.slice(1).every((dates) => dates.has(date)))
      .sort((a, b) => b.localeCompare(a));
  }, [recordsByPlatform]);

  const latestDates = useMemo<Record<PlatformId, string>>(() => ({
    tmall: recordsByPlatform.tmall.reduce((latest, item) => item.periodEnd > latest ? item.periodEnd : latest, ""),
    kuaishou: recordsByPlatform.kuaishou.reduce((latest, item) => item.periodEnd > latest ? item.periodEnd : latest, ""),
    pdd: recordsByPlatform.pdd.reduce((latest, item) => item.periodEnd > latest ? item.periodEnd : latest, ""),
    douyin: recordsByPlatform.douyin.reduce((latest, item) => item.periodEnd > latest ? item.periodEnd : latest, ""),
  }), [recordsByPlatform]);

  const activeDate = commonDates.includes(selectedDate)
    ? selectedDate
    : (commonDates[0] ?? "");
  const earliestCommonDate = commonDates.at(-1) ?? "";
  const latestCommonDate = commonDates[0] ?? "";
  const defaultRangeStart = commonDates[Math.min(6, Math.max(commonDates.length - 1, 0))] ?? activeDate;
  const rangeStart = customRange.start || defaultRangeStart;
  const rangeEnd = customRange.end || activeDate;
  const periodStart = periodMode === "custom" ? rangeStart : periodMode === "day" ? activeDate : "";
  const periodEnd = periodMode === "custom" ? rangeEnd : periodMode === "day" ? activeDate : "";
  const periodLabel = periodMode === "latest"
    ? "各平台最新一天"
    : periodStart && periodEnd
      ? periodStart === periodEnd
        ? formatDate(periodStart)
        : `${formatDate(periodStart)} 至 ${formatDate(periodEnd)}`
      : "暂无共同日期";
  const periodKey = periodMode === "latest"
    ? `latest-${Object.values(latestDates).filter(Boolean).sort().join("-")}`
    : periodStart === periodEnd ? periodStart : `${periodStart}-${periodEnd}`;
  const currentRecords = useMemo(
    () =>
      (Object.keys(recordsByPlatform) as PlatformId[]).flatMap((platform) =>
        recordsByPlatform[platform]
          .filter((item) => periodMode === "latest"
            ? item.periodEnd === latestDates[platform]
            : item.periodEnd >= periodStart && item.periodEnd <= periodEnd)
          .map((item) => ({ ...item, platform })),
      ),
    [latestDates, periodEnd, periodMode, periodStart, recordsByPlatform],
  );
  const storeCounts = useMemo<Record<PlatformId, number>>(() => ({
    tmall: storesByPlatform.tmall.length,
    kuaishou: storesByPlatform.kuaishou.length,
    pdd: storesByPlatform.pdd.length,
    douyin: storesByPlatform.douyin.length,
  }), [storesByPlatform]);
  const overall = summarize(currentRecords);
  const platformRows = (Object.keys(platformLabels) as PlatformId[]).map((platform) => {
    const records = currentRecords.filter((item) => item.platform === platform);
    const uniqueAgents = groupUniqueAgents(records);
    return {
      platform,
      records,
      stores: storeCounts[platform],
      latestDate: latestDates[platform],
      agents: uniqueAgents.length,
      uniqueAgents,
      summary: summarize(records),
    };
  });

  const allAgentRows = useMemo(() => {
    const groups = new Map<string, PlatformRecord[]>();
    currentRecords.forEach((record) => {
      const key = `${record.platform}\u0000${record.storeName}\u0000${record.agentName}`;
      groups.set(key, [...(groups.get(key) ?? []), record]);
    });
    return [...groups.entries()]
      .map(([key, records]) => ({
        key,
        platform: records[0]!.platform,
        storeName: records[0]!.storeName,
        agentName: records[0]!.agentName,
        summary: summarize(records),
      }));
  }, [currentRecords]);
  const agentRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allAgentRows.filter((row) => !term || `${platformLabels[row.platform]}${row.storeName}${row.agentName}`.toLowerCase().includes(term));
  }, [allAgentRows, search]);

  const isLoading = tmall.isLoading || kuaishou.isLoading || pdd.isLoading || douyin.isLoading;
  const isError = tmall.isError || kuaishou.isError || pdd.isError || douyin.isError;
  const retry = () => void Promise.all([tmall.refetch(), kuaishou.refetch(), pdd.refetch(), douyin.refetch()]);

  if (isLoading) {
    return <main className="main"><div className="centerState"><RefreshCw className="spin" />正在汇总四个平台数据...</div></main>;
  }
  if (isError) {
    return <main className="main"><div className="centerState errorState"><AlertTriangle />无法读取全部平台数据<button className="button" onClick={retry}>重新加载</button></div></main>;
  }

  const storeCount = platformRows.reduce((sum, row) => sum + row.stores, 0);
  const agentCount = platformRows.reduce((sum, row) => sum + row.agents, 0);
  const demoCount = (tmall.data?.records.filter((item) => item.dataSource === "DEMO").length ?? 0)
    + (kuaishou.data?.records.filter((item) => item.dataSource === "DEMO").length ?? 0)
    + (pdd.data?.records.filter((item) => item.dataSource === "DEMO").length ?? 0)
    + (douyin.data?.records.filter((item) => item.dataSource === "DEMO").length ?? 0);
  const metricTotal = (metric: OverviewMetric) => metric === "platformCount" ? "4" : metric === "storeCount" ? formatNumber(storeCount) : metric === "agentCount" ? formatNumber(agentCount) : metric === "receptionCount" ? formatNumber(overall.receptionCount) : metric === "orderCount" ? formatNumber(overall.orderCount) : formatMoney(overall.netSalesAmount);
  const metricRows = (metric: OverviewMetric): MetricDetailRow[] => {
    if (metric === "platformCount") return platformRows.map((row) => ({ key: row.platform, name: platformLabels[row.platform], store: "平台汇总", platform: platformLabels[row.platform], value: `${row.stores} 店 / ${row.agents} 人` }));
    if (metric === "storeCount") return storeDirectoryRows.map((store) => ({ key: `${store.platform}:${store.id}`, name: store.name, store: store.hasData ? "已有数据" : "待同步", platform: platformLabels[store.platform], value: store.latestDate ? formatDate(store.latestDate) : "--" }));
    if (metric === "agentCount") return platformRows.flatMap((row) => row.uniqueAgents.map((agent) => ({ key: agent.key, name: agent.names.join(" / "), store: agent.stores.join("、"), platform: platformLabels[agent.platform], value: "1 人" })));
    return allAgentRows.map((row) => ({ key: row.key, name: row.agentName, store: row.storeName, platform: platformLabels[row.platform], value: metric === "receptionCount" ? formatNumber(row.summary.receptionCount) : metric === "orderCount" ? (row.summary.orderAvailable ? formatNumber(row.summary.orderCount) : "--") : formatMoney(row.summary.netSalesAmount) }));
  };
  const metricCardProps = (metric: OverviewMetric, className = "") => ({
    className,
    role: "button" as const,
    tabIndex: 0,
    "aria-label": `打开${overviewMetricLabels[metric]}明细窗口`,
    onClick: () => setMetricOpen(metric),
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setMetricOpen(metric); }
    },
  });
  const selectPeriodMode = (mode: OverviewPeriodMode) => {
    setPeriodMode(mode);
    if (mode === "custom" && !customRange.start && !customRange.end) {
      setCustomRange({ start: defaultRangeStart, end: activeDate });
    }
  };

  return (
    <main className="main tmallMain unifiedDashboardMain">
      <header className="topbar">
        <button className="menuButton" aria-label="打开导航" onClick={onMenuOpen}><Menu /></button>
        <strong>全平台数据看板</strong>
        <span>老板经营视图</span>
        <i className="liveDot" />
        <small>4 个平台已汇总</small>
      </header>
      <div className="tmallContent">
        {platformNav}
        <section className="tmallHead unifiedDashboardHead">
          <div>
            <h1>全平台客服经营数据</h1>
            <p>平台、店铺和客服名称均按原始数据展示，可继续进入各平台查看完整明细</p>
          </div>
          <div className="tmallActions">
            <button className="button" disabled={!currentRecords.length} onClick={() => exportCurrentRecords(currentRecords, periodKey)}>
              <Download />导出当前明细
            </button>
            <button className="button dangerButton" disabled={!demoCount || clearDemoMutation.isPending} onClick={() => {
              if (window.confirm("只清除全部演示/模拟数据，CSV 导入、手工真实数据和自动采集数据都会保留。确认继续？")) clearDemoMutation.mutate();
            }}><Trash2 />{clearDemoMutation.isPending ? "清除中..." : "清除全部演示数据"}</button>
          </div>
        </section>

        <section className="unifiedDashboardToolbar">
          <div>
            <strong>{periodMode === "latest" ? "各平台最新数据" : "统一统计周期"}</strong>
            <span>{periodMode === "latest" ? "每天采集完成后自动显示各平台最新一天" : "单日或自定义日期范围，四个平台使用同一时间口径"}</span>
          </div>
          <section className="unifiedPeriodControls">
            <div className="unifiedPeriodModes" role="tablist" aria-label="统计周期类型">
              <button type="button" className={periodMode === "latest" ? "active" : ""} onClick={() => selectPeriodMode("latest")}>各平台最新</button>
              <button type="button" className={periodMode === "day" ? "active" : ""} onClick={() => selectPeriodMode("day")}>单日</button>
              <button type="button" className={periodMode === "custom" ? "active" : ""} onClick={() => selectPeriodMode("custom")}>自定义</button>
            </div>
            {periodMode === "latest" ? (
              <div className="unifiedLatestDates" aria-label="各平台最新数据日期">
                {(Object.keys(platformLabels) as PlatformId[]).map((platform) => <span key={platform}>{platformLabels[platform]} {latestDates[platform] ? formatDate(latestDates[platform]) : "暂无"}</span>)}
              </div>
            ) : periodMode === "day" ? (
              <label>日期<select value={activeDate} onChange={(event) => setSelectedDate(event.target.value)}>{commonDates.map((date) => <option key={date} value={date}>{formatDate(date)}</option>)}</select></label>
            ) : (
              <div className="unifiedCustomRange">
                <label>开始日期<input type="date" min={earliestCommonDate} max={latestCommonDate} value={rangeStart} onChange={(event) => { const start = event.target.value; setCustomRange((range) => ({ start, end: range.end && range.end < start ? start : (range.end || rangeEnd) })); }} /></label>
                <span>至</span>
                <label>结束日期<input type="date" min={earliestCommonDate} max={latestCommonDate} value={rangeEnd} onChange={(event) => { const end = event.target.value; setCustomRange((range) => ({ start: range.start && range.start > end ? end : (range.start || rangeStart), end })); }} /></label>
              </div>
            )}
          </section>
        </section>

        <section className="tmallKpis unifiedKpis">
          <article {...metricCardProps("platformCount")}><div><span>平台数</span><BarChart3 /></div><strong>4</strong><small>天猫 / 快手 / 拼多多 / 抖音</small></article>
          <article {...metricCardProps("storeCount")}><div><span>店铺数</span><Store /></div><strong>{formatNumber(storeCount)}</strong><small>按平台分别统计</small></article>
          <article {...metricCardProps("agentCount")}><div><span>客服数</span><Users /></div><strong>{formatNumber(agentCount)}</strong><small>同平台同名客服或子账号合并</small></article>
          <article {...metricCardProps("receptionCount")}><div><span>有效接待人数</span><Users /></div><strong>{formatNumber(overall.receptionCount)}</strong><small>{periodLabel}</small></article>
          <article {...metricCardProps("orderCount")}><div><span>下单人数</span><TrendingUp /></div><strong>{formatNumber(overall.orderCount)}</strong><small>询单 {formatNumber(overall.inquiryCount)} 人</small></article>
          <article {...metricCardProps("netSalesAmount", "featured")}><div><span>净销售额</span><WalletCards /></div><strong>{formatMoney(overall.netSalesAmount)}</strong><small>四平台原始数据合计</small></article>
        </section>

        <section className="unifiedPlatformPanel">
          <header>
            <div><h2>平台经营明细</h2><p>经营数据按平台独立汇总，客服人数按同平台同名去重</p></div>
            <span>{periodLabel}</span>
          </header>
          <div className="unifiedPlatformTableScroll">
            <table>
              <thead><tr><th>平台</th><th>数据日期</th><th>店铺</th><th>客服</th><th>有效接待</th><th>询单 / 下单</th><th>净销售额</th><th>付款转化率</th><th>平均响应</th><th>满意度</th></tr></thead>
              <tbody>{platformRows.map((row) => (
                <tr key={row.platform}>
                  <td><span className={`platformBadge ${row.platform}`}>{platformLabels[row.platform]}</span></td>
                  <td>{periodMode === "latest" && row.latestDate ? formatDate(row.latestDate) : periodLabel}</td>
                  <td>{row.stores} 店</td>
                  <td>{row.agents} 人</td>
                  <td>{formatNumber(row.summary.receptionCount)}</td>
                  <td>{row.summary.inquiryAvailable ? formatNumber(row.summary.inquiryCount) : "--"} / {row.summary.orderAvailable ? formatNumber(row.summary.orderCount) : "--"}</td>
                  <td><strong>{formatMoney(row.summary.netSalesAmount)}</strong></td>
                  <td><strong className="tmallMetric">{formatConversion(row.summary)}</strong></td>
                  <td>{formatReply(row.summary)}</td>
                  <td>{formatSatisfaction(row.summary)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </section>

        <section className="unifiedPlatformPanel unifiedStoreDirectoryPanel">
          <header>
            <div><h2>店铺目录</h2><p>店铺名称固定保留，采集暂时失败也不会从页面消失</p></div>
            <label className="tmallSearch"><Search /><input value={storeSearch} onChange={(event) => setStoreSearch(event.target.value)} placeholder="搜索平台或店铺" /></label>
          </header>
          <div className="unifiedStoreDirectoryScroll">
            <table>
              <thead><tr><th>平台</th><th>店铺名称</th><th>数据状态</th><th>最近数据日期</th></tr></thead>
              <tbody>{filteredStoreRows.map((store) => (
                <tr key={`${store.platform}:${store.id}`}>
                  <td><span className={`platformBadge ${store.platform}`}>{platformLabels[store.platform]}</span></td>
                  <td><strong>{store.name}</strong></td>
                  <td><span className={`unifiedStoreStatus ${store.hasData ? "ready" : "pending"}`}>{store.hasData ? "已有数据" : "待同步"}</span></td>
                  <td>{store.latestDate ? formatDate(store.latestDate) : "--"}</td>
                </tr>
              ))}</tbody>
            </table>
            {!filteredStoreRows.length && <div className="operationsEmpty">没有匹配的店铺</div>}
          </div>
          <footer><span>共 {storeDirectoryRows.length} 家店铺</span><span>已有数据 {storeDirectoryRows.filter((store) => store.hasData).length} 家</span></footer>
        </section>

        <section className="tmallTablePanel unifiedAgentPanel">
          <header>
            <div><h2>客服绩效明细</h2><p>每条记录明确标注所属平台、店铺和客服名称</p></div>
            <label className="tmallSearch"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索平台、店铺或客服" /></label>
          </header>
          <div className="tmallTableScroll">
            <table>
              <thead><tr><th>平台</th><th>店铺 / 客服</th><th>接待人数</th><th>询单 / 下单</th><th>净销售额</th><th>付款转化率</th><th>响应时长</th><th>满意度</th></tr></thead>
              <tbody>{agentRows.map((row) => (
                <tr key={row.key}>
                  <td><span className={`platformBadge ${row.platform}`}>{platformLabels[row.platform]}</span></td>
                  <td><strong>{row.agentName}</strong><small>{row.storeName}</small></td>
                  <td>{formatNumber(row.summary.receptionCount)}</td>
                  <td>{row.summary.inquiryAvailable ? formatNumber(row.summary.inquiryCount) : "--"} / {row.summary.orderAvailable ? formatNumber(row.summary.orderCount) : "--"}</td>
                  <td><strong>{formatMoney(row.summary.netSalesAmount)}</strong></td>
                  <td><strong className="tmallMetric">{formatConversion(row.summary)}</strong></td>
                  <td>{formatReply(row.summary)}</td>
                  <td>{formatSatisfaction(row.summary)}</td>
                </tr>
              ))}</tbody>
            </table>
            {!agentRows.length && <div className="operationsEmpty">当前日期没有匹配数据</div>}
          </div>
          <footer><span>共 {agentRows.length} 条客服明细</span><span>统计周期：{periodLabel}</span></footer>
        </section>
      </div>
      {metricOpen && <MetricDetailModal title={overviewMetricLabels[metricOpen]} period={periodLabel} total={metricTotal(metricOpen)} rows={metricRows(metricOpen)} onClose={() => setMetricOpen(null)} />}
    </main>
  );
}

export function UnifiedDashboardPage({ onMenuOpen }: { onMenuOpen: () => void }) {
  const [view, setView] = useState<DashboardView>("all");
  const platformNav = <DashboardPlatformTabs current={view} onChange={setView} />;

  if (view === "tmall") {
    return <TmallDashboardPage onMenuOpen={onMenuOpen} platformNav={platformNav} />;
  }
  if (view === "kuaishou") {
    return <KuaishouDashboardPage onMenuOpen={onMenuOpen} platformNav={platformNav} />;
  }
  if (view === "pdd") {
    return <PddDashboardPage onMenuOpen={onMenuOpen} platformNav={platformNav} />;
  }
  if (view === "douyin") {
    return <DouyinDashboardPage onMenuOpen={onMenuOpen} platformNav={platformNav} />;
  }
  return <AllPlatformsOverview onMenuOpen={onMenuOpen} platformNav={platformNav} />;
}
